import React, { useState } from 'react';
import { useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import useAuth from '../../Hooks/useAuth';
import { ManagedDataSetColumnFormat } from '../Shared/ManagedDataSet';
import {
    ManagedFormRule,
    ManagedFormErrorInfo,
    ManagedApiError,
    ManagedAdminEditorMode,
    ManagedAdminActionMode,
    ManagedAdminChildOperation,
    ManagedActionModal,
    ManagedActionModalState,
    buildManagedActionModalState,
 } from '../Shared/ManagedHelpers';
import {
    AddIcon,
    Button,
    Checkbox,
    Col,
    DeleteIcon,
    Divider,
    EditIcon,
    Form,
    Input,
    Option,
    Row,
    Select,
    Space,
    Spin,
    Text,
    Title,
} from '@servahealth/serva-ui-kit';
import { ColorPicker } from 'antd';
import { SimplifiedUser } from '../Shared/SimplifiedUser';
import GetUserAdminModelsRequest from '../../Interfaces/Requests/Administration/GetUserAdminModelsRequest';
import GetUserAdminModelsResponse from '../../Interfaces/Responses/Administration/GetUserAdminModelResponse';
import Administration from '../../Constants/Endpoints/Administration';

export type ManagedDataSetColumnFormatEditResult = {
    operation: ManagedAdminChildOperation;
    dataSetColumnFormat: ManagedDataSetColumnFormat;
};

type AUDDataSetColumnFormatFormValues = {
    dscfColumnName: string;
    dscfFormat: string;
    dscfAggregate: string;
    dscfAllowFiltering: boolean;
    dscfGroupColumn: boolean;
    dscfIncludeInGrandTotal: boolean;
    dscfVisible: boolean;
    dscfAlignment: string;
    dscfColorBG: string | undefined;
    dscfColorFore: string | undefined;
    dscfFormatExcel: string;
    dscfColumnTitle: string;
    dscfToolTip: string;
    dscfUserID: number;
    dscfWidthExcel: number;
    dscfAllowHTML: boolean;
    dscfJSDataType: string;
};

type AUDDataSetColumnFormatsProps = {
    dataSetID: number;
    dataSetName: string;
    dataSetColumnFormats: ManagedDataSetColumnFormat[];
    initialSelectedDataSetColumnFormatID?: number;
    initialMode?: ManagedAdminActionMode;
    handleSetDataSetColumnFormat: (
        result: ManagedDataSetColumnFormatEditResult
    ) => void;
    close: () => void;
};

const AUDDataSetColumnFormats: React.FC<AUDDataSetColumnFormatsProps> = ({ close, ...props }) => {
    const { user } = useAuth();
    const [formAUDDataSetColumnFormat] = Form.useForm();

    const [dscfUsersIncludeDisabled] = useState(false);

    const dataSetColumnFormats = props.dataSetColumnFormats ?? [];

    const getInitialDSCFMode = (): ManagedAdminActionMode => {
        switch (props.initialMode) {
            case 'update':
                return 'update';
            case 'delete':
                return 'delete';
            case 'add':
            default:
                return 'add';
        }
    };

    const getInitialSelectedDataSetColumnFormat = (): ManagedDataSetColumnFormat | undefined => {
        if (!props.initialSelectedDataSetColumnFormatID || props.initialSelectedDataSetColumnFormatID <= 0) {
            return undefined;
        }

        return dataSetColumnFormats.find(
            (dataSetColumnFormat) => dataSetColumnFormat.id === props.initialSelectedDataSetColumnFormatID
        );
    };

    const initialSelectedDataSetColumnFormat = getInitialSelectedDataSetColumnFormat();
    const initialDSCFMode = getInitialDSCFMode();

    const initialDSCFFormValues = initialDSCFMode === 'add'
        ? {
            dscfColumnName: '',
            dscfFormat: '',
            dscfAggregate: '',
            dscfAllowFiltering: false,
            dscfGroupColumn: false,
            dscfIncludeInGrandTotal: false,
            dscfVisible: true,
            dscfAlignment: 'center',
            dscfColorBG: undefined,
            dscfColorFore: undefined,
            dscfFormatExcel: '',
            dscfColumnTitle: '',
            dscfToolTip: '',
            dscfUserID: 0,
            dscfWidthExcel: 0,
            dscfAllowHTML: false,
            dscfJSDataType: 'string',
        }
        : {
            dscfColumnName: initialSelectedDataSetColumnFormat?.columnName ?? '',
            dscfFormat: initialSelectedDataSetColumnFormat?.format ?? '',
            dscfAggregate: initialSelectedDataSetColumnFormat?.aggregate ?? '',
            dscfAllowFiltering: initialSelectedDataSetColumnFormat?.allowFiltering ?? false,
            dscfGroupColumn: initialSelectedDataSetColumnFormat?.groupColumn ?? false,
            dscfIncludeInGrandTotal: initialSelectedDataSetColumnFormat?.includeInGrandTotal ?? false,
            dscfVisible: initialSelectedDataSetColumnFormat?.visible ?? true,
            dscfAlignment: initialSelectedDataSetColumnFormat?.alignment ?? 'center',
            dscfColorBG: initialSelectedDataSetColumnFormat?.colorBG ?? undefined,
            dscfColorFore: initialSelectedDataSetColumnFormat?.colorFore ?? undefined,
            dscfFormatExcel: initialSelectedDataSetColumnFormat?.formatExcel ?? '',
            dscfColumnTitle: initialSelectedDataSetColumnFormat?.columnTitle ?? '',
            dscfToolTip: initialSelectedDataSetColumnFormat?.toolTip ?? '',
            dscfUserID: initialSelectedDataSetColumnFormat?.userId ?? 0,
            dscfWidthExcel: initialSelectedDataSetColumnFormat?.widthExcel ?? 0,
            dscfAllowHTML: initialSelectedDataSetColumnFormat?.allowHTML ?? false,
            dscfJSDataType: initialSelectedDataSetColumnFormat?.jsDataType ?? 'string',
        };

    const [addButtonClicked, setAddButtonClicked] = useState(initialDSCFMode === 'add');
    const [updButtonClicked, setUpdButtonClicked] = useState(initialDSCFMode === 'update');
    const [delButtonClicked, setDelButtonClicked] = useState(initialDSCFMode === 'delete');

    const [dscfSelectedDataSetColumnFormatID, setDSCFSelectedDataSetColumnFormatID] = useState(initialSelectedDataSetColumnFormat?.id ?? -1);
    const [dscfSelectedDataSetColumnFormatName, setDSCFSelectedDataSetColumnFormatName] = useState(
        initialSelectedDataSetColumnFormat?.columnName ?? ''
    );

    const [dscfAggregate, setDSCFAggregate] = useState<string>(initialSelectedDataSetColumnFormat?.aggregate ?? '');
    const [dscfAlignment, setDSCFAlignment] = useState<string>(initialSelectedDataSetColumnFormat?.alignment ?? 'center');
    const [dscfJSDataType, setDSCFJSDataType] = useState<string>(initialSelectedDataSetColumnFormat?.jsDataType ?? 'string');
    const [dscfUserID, setDSCFUserID] = useState<number>(initialSelectedDataSetColumnFormat?.userId ?? 0);
    const [dscfColorFore, setDSCFColorFore] = useState<string | undefined>(initialSelectedDataSetColumnFormat?.colorFore ?? undefined);
    const [dscfColorBG, setDSCFColorBG] = useState<string | undefined>(initialSelectedDataSetColumnFormat?.colorBG ?? undefined);

    const [dscfSelectedDataSetColumnFormatCreatedBy, setDSCFSelectedDataSetColumnFormatCreatedBy] = useState(initialSelectedDataSetColumnFormat?.createdBy ?? -1);
    const [dscfSelectedDataSetColumnFormatCreatedTs, setDSCFSelectedDataSetColumnFormatCreatedTs] = useState(initialSelectedDataSetColumnFormat?.createdTs ?? new Date());

    const dscfPropsDataSetID = props.dataSetID > 0 ? props.dataSetID : 0;
    const dscfPropsDataSetName = props.dataSetName !== '' ? props.dataSetName : 'New DataSet';

    const [managedActionModalState, setManagedActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const isBlank = (value: unknown): boolean => {
        return value === undefined || value === null || String(value).trim().length === 0;
    };

    const validateRequiredDSCFField = (fieldName: string): Promise<void> => {
        const value = formAUDDataSetColumnFormat.getFieldValue(fieldName);
        return isBlank(value) ? Promise.reject() : Promise.resolve();
    };

    const validateDSCFDetailsData = (rule: ManagedFormRule, value: string) => {
        switch (rule.field) {
            case 'dscfColumnName':
                return validateRequiredDSCFField(rule.field);

            case 'dscfFormat':
            case 'dscfFormatExcel':
            case 'dscfAggregate':
            case 'dscfAlignment':
            case 'dscfJSDataType':
            case 'dscfColumnTitle':
            case 'dscfToolTip':
            case 'dscfWidthExcel':
                return Promise.resolve();

            default:
                return Promise.reject();
        }
    };

    const { data: users = [], isLoading: isGetUsersLoading } = useQuery<GetUserAdminModelsResponse[], Error, SimplifiedUser[]>(
        ['getUserAdminModelsRequest', dscfUsersIncludeDisabled],
        async (): Promise<GetUserAdminModelsResponse[]> => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_USER_ADMIN_MODELS}`;
                const requestData: GetUserAdminModelsRequest = { gua_includeDisabled: dscfUsersIncludeDisabled };
                return await AxiosUtil.get<GetUserAdminModelsResponse[], GetUserAdminModelsRequest>(url, requestData);
            } catch (err) {
                console.error('GET_USER_ADMIN_MODELS: [Get User Admin Models] Internal Error: ', err);
                throw err;
            }
        },
        {
            onError: (err: Error) => {
                handleDSCFLoadError('GET_USER_ADMIN_MODELS: [Get User Admin Models] Internal Error', err);
            },
            select: (response: GetUserAdminModelsResponse[]): SimplifiedUser[] => {
                return response
                    .map((eachElement): SimplifiedUser => {
                        return {
                            id: eachElement.id,
                            firstName: eachElement.firstName,
                            lastName: eachElement.lastName,
                            email: eachElement.email,
                            password: eachElement.password,
                            userName: eachElement.userName,
                            phone: eachElement.phone,
                            adminComment: eachElement.adminComment,
                            changePassword: eachElement.changePasswordRequired,
                            reportsOnly: eachElement.reportsOnly,
                            isSystemAccount: eachElement.isSystemAccount,
                            isDemo: eachElement.isDemo,
                            securityAdmin: eachElement.securityAdmin,
                            techTeam: eachElement.techTeam,
                            deleted: eachElement.deleted,
                        };
                    })
                    .filter((eachUser) => !eachUser.deleted)
                    .sort((a, b) => a.email.localeCompare(b.email));
            },
        }
    );

    const handleDSCFAggregateChange = (value: string) => {
        setDSCFAggregate(value);
    };

    const handleDSCFAlignmentChange = (value: string) => {
        setDSCFAlignment(value);
    };

    const handleDSCFJSDataTypeChange = (value: string) => {
        setDSCFJSDataType(value);
    };

    const handleDSCFUserIDChange = (value: number) => {
        setDSCFUserID(value);
    };

    const handleDSCFColorForeChange = (color: any) => {
        const returnColor = (!color || color.cleared) ? 'rgb(0,0,0)' : color.toRgbString();

        setDSCFColorFore(returnColor);
        
        return returnColor;
    };

    const handleDSCFColorBGChange = (color: any) => {
        const returnColor = (!color || color.cleared) ? 'rgb(255,255,255)' : color.toRgbString();

        setDSCFColorBG(returnColor);
        
        return returnColor;
    };

    const buildManagedDataSetColumnFormatRequest = (values: AUDDataSetColumnFormatFormValues): ManagedDataSetColumnFormat => {
        return {
            id: addButtonClicked ? 0 : (updButtonClicked ? dscfSelectedDataSetColumnFormatID : (delButtonClicked ? dscfSelectedDataSetColumnFormatID : 0)),
            dataSetId: dscfPropsDataSetID,
            columnName: values.dscfColumnName,
            format: values.dscfFormat,
            aggregate: values.dscfAggregate,
            allowFiltering: values.dscfAllowFiltering,
            groupColumn: values.dscfGroupColumn,
            includeInGrandTotal: values.dscfIncludeInGrandTotal,
            visible: values.dscfVisible,
            createdBy: addButtonClicked ? (user?.id ?? 0) : (updButtonClicked ? dscfSelectedDataSetColumnFormatCreatedBy : (delButtonClicked ? dscfSelectedDataSetColumnFormatCreatedBy : 0)),
            modifiedBy: addButtonClicked ? (user?.id ?? 0) : (updButtonClicked ? (user?.id ?? 0) : (delButtonClicked ? (user?.id ?? 0) : 0)),
            deletedBy: addButtonClicked ? 0 : (updButtonClicked ? 0 : (delButtonClicked ? (user?.id ?? 0) : 0)),
            createdTs: addButtonClicked ? (new Date()) : (updButtonClicked ? dscfSelectedDataSetColumnFormatCreatedTs : (delButtonClicked ? dscfSelectedDataSetColumnFormatCreatedTs : undefined)),
            modifiedTs: addButtonClicked ? (new Date()) : (updButtonClicked ? (new Date()) : (delButtonClicked ? (new Date()) : undefined)),
            deletedTs: addButtonClicked ? (undefined) : (updButtonClicked ? (undefined) : (delButtonClicked ? (new Date()) : undefined)),
            alignment: values.dscfAlignment,
            colorBG: StripRGBString(values.dscfColorBG ?? MakeRGBString('')),
            colorFore: StripRGBString(values.dscfColorFore ?? MakeRGBString('')),
            formatExcel: values.dscfFormatExcel,
            columnTitle: values.dscfColumnTitle,
            toolTip: values.dscfToolTip,
            userId: values.dscfUserID === 0 ? null : values.dscfUserID,
            widthExcel: values.dscfWidthExcel,
            allowHTML: values.dscfAllowHTML,
            jsDataType: values.dscfJSDataType,
        };
    };

    const handleCloseManagedActionModal = () => {
        setManagedActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));
    };

    const onFinishFailed = (errorInfo: ManagedFormErrorInfo<AUDDataSetColumnFormatFormValues>) => {
        console.error('AUDDataSetColumnFormats validation failed:', errorInfo);

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'DataSet Column Format Validation',
                'Please correct the highlighted DataSet Column Format fields and try again.',
                true
            )
        );
    };

    const onFinish = (values: AUDDataSetColumnFormatFormValues) => {
        const newManagedDataSetColumnFormat = buildManagedDataSetColumnFormatRequest(values);

        props.handleSetDataSetColumnFormat({
            operation: getDSCFOperation(),
            dataSetColumnFormat: newManagedDataSetColumnFormat,
        });

        close();
    };

    const setDSCFMode = (mode: ManagedAdminActionMode) => {
        setAddButtonClicked(mode === 'add');
        setUpdButtonClicked(mode === 'update');
        setDelButtonClicked(mode === 'delete');
    };

    const clearDSCFSelection = () => {
        setDSCFSelectedDataSetColumnFormatID(-1);
        setDSCFSelectedDataSetColumnFormatName('');
        setDSCFSelectedDataSetColumnFormatCreatedBy(0);
        setDSCFSelectedDataSetColumnFormatCreatedTs(new Date());

        setDSCFAggregate('');
        setDSCFAlignment('');
        setDSCFJSDataType('');
        setDSCFUserID(0);
        setDSCFColorBG(MakeRGBString(''));
        setDSCFColorFore(MakeRGBString(''));

        setDSCFMode('add');
        handleDSCFDataSetColumnFormatResetForm('blank');
    };

    const handleDSCFSelectionChange = (value: number) => {
        setDSCFSelectedDataSetColumnFormatID(value);

        const selectedDataSetColumnFormat = dataSetColumnFormats.find((dataSetColumnFormat) => (dataSetColumnFormat.id === value));

        setDSCFSelectedDataSetColumnFormatName(selectedDataSetColumnFormat?.columnName ?? '');

        setDSCFSelectedDataSetColumnFormatCreatedBy(selectedDataSetColumnFormat?.createdBy ?? 0);
        setDSCFSelectedDataSetColumnFormatCreatedTs(selectedDataSetColumnFormat?.createdTs ?? new Date());

        setDSCFMode('add');
        handleDSCFDataSetColumnFormatResetForm('blank');
    };

    const MakeRGBString = (color: string) => {
        if (color === '') {
            return 'rgb(0,0,0)';
        }
        else {
            return 'rgb(' + color + ')';
        }
    }

    const StripRGBString = (rgbString: string) => {
        return rgbString.slice(4, -1);
    }

    const setDSCFBlankForm = () => {
        formAUDDataSetColumnFormat.setFieldValue('dscfColumnName', '');
        formAUDDataSetColumnFormat.setFieldValue('dscfFormat', '');
        formAUDDataSetColumnFormat.setFieldValue('dscfAggregate', '');
        formAUDDataSetColumnFormat.setFieldValue('dscfAllowFiltering', false);
        formAUDDataSetColumnFormat.setFieldValue('dscfGroupColumn', false);
        formAUDDataSetColumnFormat.setFieldValue('dscfIncludeInGrandTotal', false);
        formAUDDataSetColumnFormat.setFieldValue('dscfVisible', true);
        formAUDDataSetColumnFormat.setFieldValue('dscfAlignment', '');
        formAUDDataSetColumnFormat.setFieldValue('dscfColorBG', MakeRGBString(''));
        formAUDDataSetColumnFormat.setFieldValue('dscfColorFore', MakeRGBString(''));
        formAUDDataSetColumnFormat.setFieldValue('dscfFormatExcel', '');
        formAUDDataSetColumnFormat.setFieldValue('dscfColumnTitle', '');
        formAUDDataSetColumnFormat.setFieldValue('dscfToolTip', '');
        formAUDDataSetColumnFormat.setFieldValue('dscfUserID', 0);
        formAUDDataSetColumnFormat.setFieldValue('dscfWidthExcel', 0);
        formAUDDataSetColumnFormat.setFieldValue('dscfAllowHTML', false);
        formAUDDataSetColumnFormat.setFieldValue('dscfJSDataType', '');

        setDSCFAggregate('');
        setDSCFAlignment('');
        setDSCFJSDataType('');
        setDSCFUserID(0);
        setDSCFColorBG(MakeRGBString(''));
        setDSCFColorFore(MakeRGBString(''));
    };

    const setDSCFFormFromSelectedDataSetColumnFormat = () => {
        const selectedDataSetColumnFormat = dataSetColumnFormats.find(
            (dataSetColumnFormat) => dataSetColumnFormat.id === dscfSelectedDataSetColumnFormatID
        );

        formAUDDataSetColumnFormat.setFieldValue('dscfColumnName', selectedDataSetColumnFormat?.columnName ?? '');
        formAUDDataSetColumnFormat.setFieldValue('dscfFormat', selectedDataSetColumnFormat?.format ?? '');
        formAUDDataSetColumnFormat.setFieldValue('dscfAggregate', selectedDataSetColumnFormat?.aggregate ?? '');
        formAUDDataSetColumnFormat.setFieldValue('dscfAllowFiltering', selectedDataSetColumnFormat?.allowFiltering ?? false);
        formAUDDataSetColumnFormat.setFieldValue('dscfGroupColumn', selectedDataSetColumnFormat?.groupColumn ?? false);
        formAUDDataSetColumnFormat.setFieldValue('dscfIncludeInGrandTotal', selectedDataSetColumnFormat?.includeInGrandTotal ?? false);
        formAUDDataSetColumnFormat.setFieldValue('dscfVisible', selectedDataSetColumnFormat?.visible ?? true);
        formAUDDataSetColumnFormat.setFieldValue('dscfAlignment', selectedDataSetColumnFormat?.alignment ?? '');
        formAUDDataSetColumnFormat.setFieldValue('dscfColorBG', MakeRGBString(selectedDataSetColumnFormat?.colorBG ?? ''));
        formAUDDataSetColumnFormat.setFieldValue('dscfColorFore', MakeRGBString(selectedDataSetColumnFormat?.colorFore ?? ''));
        formAUDDataSetColumnFormat.setFieldValue('dscfFormatExcel', selectedDataSetColumnFormat?.formatExcel ?? '');
        formAUDDataSetColumnFormat.setFieldValue('dscfColumnTitle', selectedDataSetColumnFormat?.columnTitle ?? '');
        formAUDDataSetColumnFormat.setFieldValue('dscfToolTip', selectedDataSetColumnFormat?.toolTip ?? '');
        formAUDDataSetColumnFormat.setFieldValue('dscfUserID', selectedDataSetColumnFormat?.userId ?? 0);
        formAUDDataSetColumnFormat.setFieldValue('dscfWidthExcel', selectedDataSetColumnFormat?.widthExcel ?? 0);
        formAUDDataSetColumnFormat.setFieldValue('dscfAllowHTML', selectedDataSetColumnFormat?.allowHTML ?? false);
        formAUDDataSetColumnFormat.setFieldValue('dscfJSDataType', selectedDataSetColumnFormat?.jsDataType ?? '');

        setDSCFAggregate(selectedDataSetColumnFormat?.aggregate ?? '');
        setDSCFAlignment(selectedDataSetColumnFormat?.alignment ?? '');
        setDSCFJSDataType(selectedDataSetColumnFormat?.jsDataType ?? '');
        setDSCFUserID(selectedDataSetColumnFormat?.userId ?? 0);
        setDSCFColorBG(MakeRGBString(selectedDataSetColumnFormat?.colorBG ?? ''));
        setDSCFColorFore(MakeRGBString(selectedDataSetColumnFormat?.colorFore ?? ''));
    };

    const getDSCFOperation = (): ManagedAdminChildOperation => {
        if (addButtonClicked) return 'add';
        if (updButtonClicked) return 'update';
        return 'delete';
    };

    const getDSCFActionColor = (): string => {
        if (addButtonClicked) return 'cornflowerblue';
        if (updButtonClicked) return 'darkgray';
        if (delButtonClicked) return 'red';
        return 'black';
    };

    const handleDSCFDataSetColumnFormatResetForm = (mode: ManagedAdminEditorMode) => {
        if (mode === 'blank') {
            setDSCFBlankForm();
            return;
        }

        setDSCFFormFromSelectedDataSetColumnFormat();
    };

    //=================================================================
    //=================================================================
    
    const handleDSCFAddDataSetColumnFormat = () => {
        clearDSCFSelection();
    };

    //=================================================================
    //=================================================================

    const handleDSCFUpdDataSetColumnFormat = () => {
        setDSCFMode('update');
        handleDSCFDataSetColumnFormatResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const handleDSCFDelDataSetColumnFormat = () => {
        setDSCFMode('delete');
        handleDSCFDataSetColumnFormatResetForm('selected');
    };

    //=================================================================
    //=================================================================

    const onDSCFCancel = () => {
        clearDSCFSelection();
    };

    const handleDSCFLoadError = (title: string, err: unknown) => {
        const apiError = err as ManagedApiError;

        const message =
            apiError?.response?.data?.message
                ? `${title}: ${apiError.response.data.message}`
                : `${title}: ${String(err)}`;

        console.error(title, err);

        setManagedActionModalState(
            buildManagedActionModalState(
                'info',
                'Error',
                message,
                true
            )
        );
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '95%',
        textAlign: 'center',
        color: 'black',
        backgroundColor: 'azure',
        borderRadius: '10px',
        border: `1px solid black`,
        marginTop: '16px',
        paddingRight: '8px',
        paddingLeft: '8px',
        height: '375px'
    };

    return (
        <>
            <ManagedActionModal
                modalState={managedActionModalState}
                onOk={handleCloseManagedActionModal}
            />
            <Spin spinning={isGetUsersLoading}>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '4px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage DataSet Column Formats</Title>
                    </Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'DataSet: [' + dscfPropsDataSetID + '] ' + dscfPropsDataSetName}</Text>
                </Space>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '2px', width: '100%'}}>
                    <Text italic type='secondary'>{'DataSet Column Format: [' + dscfSelectedDataSetColumnFormatID + '] ' + dscfSelectedDataSetColumnFormatName}</Text>
                </Space>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={1} />
                    <Col span={22}>
                        <div>
                            <Space direction='horizontal' style={{width: '100%', justifyContent: 'left'}}>
                                <Text italic>Select an action below to proceed.</Text>
                            </Space>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                    <Col span={1} />
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={2} />
                    <Col span={8}>
                        <Text italic style={{float: 'right'}}>Select DataSet Column Format to Update or Delete:</Text>
                    </Col>
                    <Col span={14}>
                        <Select
                            styles={{ popup: { root: { width: 500 } } }}
                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                            placeholder={'Select a DataSet Column Format'}
                            showSearch
                            style={{paddingTop: '4px', width: '95%'}}
                            onChange={handleDSCFSelectionChange}
                        >
                            {dataSetColumnFormats.map(
                                (eachDataSetColumnFormat) => {
                                    return (
                                        <Option
                                            key={eachDataSetColumnFormat.id}
                                            label={eachDataSetColumnFormat.columnName}
                                            value={eachDataSetColumnFormat.id}
                                        >
                                            {eachDataSetColumnFormat.columnName}
                                        </Option>
                                    );
                                }
                            )}
                        </Select>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={6}>
                        <Button
                            icon={<AddIcon />}
                            style={{ float: 'right' , marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleDSCFAddDataSetColumnFormat}
                        >
                            Add DataSet Column Format
                        </Button>
                    </Col>
                    <Col span={6} />
                    <Col span={6}>
                        <Button
                            disabled={dscfSelectedDataSetColumnFormatID === -1}
                            icon={<EditIcon />}
                            style={{ backgroundColor: 'darkgray', float: 'right', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleDSCFUpdDataSetColumnFormat}
                        >
                            Update DataSet Column Format
                        </Button>
                    </Col>
                    <Col span={6}>
                        <Button
                            disabled={dscfSelectedDataSetColumnFormatID === -1}
                            icon={<DeleteIcon />}
                            style={{ backgroundColor: 'red', float: 'right', marginBottom: '4px', width: '100%' }}
                            type='primary'
                            onClick={handleDSCFDelDataSetColumnFormat}
                        >
                            Delete DataSet Column Format
                        </Button>
                    </Col>
                </Row>
                <Row gutter={16} className='retainbi-compact-row'>
                    <Col span={24}>
                        <div>
                            <Divider style={{ borderTop: '1px solid black', margin: '1px' }}></Divider>
                        </div>
                    </Col>
                </Row>
                <Form
                    form={formAUDDataSetColumnFormat}
                    initialValues={initialDSCFFormValues}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                >
                    <div style={contentStyle}>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={21}>
                                <Divider orientation='left' orientationMargin={30} variant='dotted' style={{ borderColor: 'black', color: getDSCFActionColor() }}>
                                    {addButtonClicked ? 'Add DataSet Column Format' :
                                        (updButtonClicked ? 'Update DataSet Column Format: [' + dscfSelectedDataSetColumnFormatID + '] ' :
                                            (delButtonClicked ? 'Delete DataSet Column Format: [' + dscfSelectedDataSetColumnFormatID + '] ' : ''))}
                                </Divider>
                            </Col>
                            <Col span={3}>
                                <Button
                                    disabled={delButtonClicked}
                                    style={{ color: getDSCFActionColor(), fontStyle: 'italic' }}
                                    type='link'
                                    onClick={() => handleDSCFDataSetColumnFormatResetForm(addButtonClicked ? 'blank' : 'selected')}
                                >
                                    Reset Form
                                </Button>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={8}>
                                <Form.Item
                                    key='dscfcolumnname'
                                    name='dscfColumnName'
                                    label='Column Name'
                                    labelCol={{ span: 8, }}
                                    rules={[
                                        {
                                            message: 'Column Name required.',
                                            required: true,
                                            validator: validateDSCFDetailsData,
                                        },
                                    ]}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Column Name'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    key='dscfcolumntitle'
                                    label='Column Title'
                                    labelCol={{ span: 8, }}
                                    name='dscfColumnTitle'
                                    rules={[
                                        {
                                            validator: validateDSCFDetailsData,
                                        },
                                    ]}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Column Title'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    key='dscfformat'
                                    label='Format'
                                    labelCol={{ span: 8, }}
                                    name='dscfFormat'
                                    rules={[
                                        {
                                            validator: validateDSCFDetailsData,
                                        },
                                    ]}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Format'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={6}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        key='dscfaggregate'
                                        name='dscfAggregate'
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            label='Aggregate:'
                                            placeholder='Select Aggregate Type'
                                            showSearch
                                            value={dscfAggregate}
                                            onChange={handleDSCFAggregateChange}
                                        >
                                            <Option
                                                value={''}
                                                label={'(Not Set)'}
                                            >
                                                {'(Not Set)'}
                                            </Option>
                                            <Option
                                                value={'AVG'}
                                                label={'Average'}
                                            >
                                                {'Average'}
                                            </Option>
                                            <Option
                                                value={'COUNT'}
                                                label={'Count'}
                                            >
                                                {'Count'}
                                            </Option>
                                            <Option
                                                value={'MAX'}
                                                label={'Maximum'}
                                            >
                                                {'Maximum'}
                                            </Option>
                                            <Option
                                                value={'MIN'}
                                                label={'Minimum'}
                                            >
                                                {'Minimum'}
                                            </Option>
                                            <Option
                                                value={'STDEV'}
                                                label={'Std. Dev.'}
                                            >
                                                {'Std. Dev.'}
                                            </Option>
                                            <Option
                                                value={'SUM'}
                                                label={'Summation'}
                                            >
                                                {'Summation'}
                                            </Option>
                                            <Option
                                                value={'VAR'}
                                                label={'Variance'}
                                            >
                                                {'Variance'}
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={6}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        key='dscfalignment'
                                        name='dscfAlignment'
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            label='Alignment:'
                                            placeholder='Select Alignment'
                                            showSearch
                                            value={dscfAlignment}
                                            onChange={handleDSCFAlignmentChange}
                                        >
                                            <Option
                                                value={'left'}
                                                label={'Left'}
                                            >
                                                {'Left'}
                                            </Option>
                                            <Option
                                                value={'center'}
                                                label={'Center'}
                                            >
                                                {'Center'}
                                            </Option>
                                            <Option
                                                value={'right'}
                                                label={'Right'}
                                            >
                                                {'Right'}
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={3}>
                                <Form.Item
                                    key='dscfcolorfore'
                                    name='dscfColorFore'
                                    label='Foreground'
                                    initialValue={'rgb(0,0,0)'}
                                    getValueFromEvent={(color, rgb) => handleDSCFColorForeChange(color)}
                                    style={{marginTop: '24px'}}
                                >
                                    <ColorPicker
                                        allowClear
                                        disabledAlpha
                                        format={'rgb'}
                                        value={dscfColorFore}
                                    >
                                    </ColorPicker>
                                </Form.Item>
                            </Col>
                            <Col span={3}>
                                <Form.Item
                                    key='dscfcolorbg'
                                    name='dscfColorBG'
                                    label='Background'
                                    initialValue={'rgb(0,0,0)'}
                                    getValueFromEvent={(color, rgb) => handleDSCFColorBGChange(color)}
                                    style={{marginTop: '24px'}}
                                >
                                    <ColorPicker
                                        allowClear
                                        disabledAlpha
                                        format={'rgb'}
                                        value={dscfColorBG}
                                    >
                                    </ColorPicker>
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        key='dscfjsdatatype'
                                        name='dscfJSDataType'
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            label='JS Data Type:'
                                            value={dscfJSDataType}
                                            showSearch
                                            onChange={handleDSCFJSDataTypeChange}
                                        >
                                            <Option
                                                value={'date'}
                                                label={'Date'}
                                            >
                                                {'Date'}
                                            </Option>
                                            <Option
                                                value={'string'}
                                                label={'String'}
                                            >
                                                {'String'}
                                            </Option>
                                            <Option
                                                value={'number'}
                                                label={'Number'}
                                            >
                                                {'Number'}
                                            </Option>
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row' style={{ justifyContent: 'center' }}>
                            <Col span={4}>
                                <Form.Item
                                    key='dscfallowfiltering'
                                    name='dscfAllowFiltering'
                                    valuePropName='checked'
                                    style={{display: 'flex', alignItems: 'center',justifyContent: 'center', marginTop: '4px', flex: 1}}
                                >
                                    <Checkbox
                                        disabled={delButtonClicked}
                                        style={{float: 'left'}}
                                    >
                                        Allow Filtering
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    key='dscfgroupcolumn'
                                    name='dscfGroupColumn'
                                    valuePropName='checked'
                                    style={{display: 'flex', alignItems: 'center',justifyContent: 'center', marginTop: '4px', flex: 1}}
                                >
                                    <Checkbox
                                        disabled={delButtonClicked}
                                        style={{float: 'left'}}
                                    >
                                        Group Column
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    key='dscfincludeingrandtotal'
                                    name='dscfIncludeInGrandTotal'
                                    valuePropName='checked'
                                    style={{display: 'flex', alignItems: 'center',justifyContent: 'center', marginTop: '4px', flex: 1}}
                                >
                                    <Checkbox
                                        disabled={delButtonClicked}
                                        style={{float: 'left'}}
                                    >
                                        Include In Grand Total
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    key='dscfvisible'
                                    name='dscfVisible'
                                    valuePropName='checked'
                                    style={{display: 'flex', alignItems: 'center',justifyContent: 'center', marginTop: '4px', flex: 1}}
                                >
                                    <Checkbox
                                        disabled={delButtonClicked}
                                        style={{float: 'left'}}
                                    >
                                        Visible
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    key='dscfallowhtml'
                                    name='dscfAllowHTML'
                                    valuePropName='checked'
                                    style={{display: 'flex', alignItems: 'center',justifyContent: 'center', marginTop: '4px', flex: 1}}
                                >
                                    <Checkbox
                                        disabled={delButtonClicked}
                                        style={{float: 'left'}}
                                    >
                                        Allow HTML
                                    </Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={8}>
                                <Form.Item
                                    key='dscftooltip'
                                    name='dscfToolTip'
                                    label='Tool Tip'
                                    labelCol={{ span: 8, }}
                                    rules={[
                                        {
                                            validator: validateDSCFDetailsData,
                                        },
                                    ]}
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Tool Tip'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    key='dscfformatexcel'
                                    name='dscfFormatExcel'
                                    label='Format Excel'
                                    labelCol={{ span: 8, }}
                                    rules={[
                                        {
                                            validator: validateDSCFDetailsData,
                                        },
                                    ]}
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Enter Format Excel'
                                        style={{color: delButtonClicked ? 'gray' : 'black'}}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    key='dscfwidthexcel'
                                    name='dscfWidthExcel'
                                    label='Width Excel'
                                    labelCol={{ span: 8, }}
                                    rules={[
                                        {
                                            validator: validateDSCFDetailsData,
                                        },
                                    ]}
                                    style={{marginBottom: '4px'}}
                                >
                                    <Input
                                        disabled={delButtonClicked}
                                        placeholder='Width Excel'
                                        style={{color: delButtonClicked ? 'gray' : 'black', float: 'left', width: '40%'}}
                                        type='number'
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16} className='retainbi-compact-row'>
                            <Col span={12}>
                                <div style={{textAlign:'left'}}>
                                    <Form.Item
                                        name='dscfUserID'
                                        style={{ marginBottom: '0px' }}
                                    >
                                        <Select
                                            disabled={delButtonClicked}
                                            filterOption={(input, option) => (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())}
                                            label='User ID:'
                                            optionFilterProp='label'
                                            showSearch
                                            value={dscfUserID}
                                            onChange={handleDSCFUserIDChange}
                                        >
                                            <Option
                                                value={0}
                                                label={'(Not Set)'}
                                            >
                                                {'(Not Set)'}
                                            </Option>
                                            {users.map(
                                                (eachUser) => {
                                                    return (
                                                        <Option
                                                            key={eachUser.id}
                                                            label={eachUser.firstName + ' ' + eachUser.lastName}
                                                            value={eachUser.id}
                                                        >
                                                            {eachUser.firstName + ' ' + eachUser.lastName + ' (' + eachUser.email + ')'}
                                                        </Option>
                                                    );
                                                }
                                            )}
                                        </Select>
                                    </Form.Item>
                                </div>
                            </Col>
                            <Col span={12} />
                        </Row>
                    </div>
                    <Row gutter={16} className='retainbi-compact-row' style={{ paddingTop: '8px' }}>
                        <Col span={6}>
                            <Form.Item
                                name='dscfCloseButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ width: '95%' }}
                                    type='default'
                                    onClick={close}
                                >
                                    Close
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={6} />
                        <Col span={6}>
                            <Form.Item
                                name='dscfApplyButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    htmlType='submit'
                                    style={{ width: '95%', backgroundColor: addButtonClicked ? 'cornflowerblue' : (updButtonClicked ? 'darkgray' : (delButtonClicked ? 'red' : 'black')) }}
                                    type='primary'
                                >
                                    {addButtonClicked ? 'Add DataSet Column Format' : (updButtonClicked ? 'Update DataSet Column Format' : (delButtonClicked ? 'Delete DataSet Column Format' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                name='dscfCancelButton'
                                style={{ marginBottom: '0px' }}
                            >
                                <Button
                                    style={{ width: '95%' }}
                                    type='default'
                                    onClick={onDSCFCancel}
                                >
                                    {addButtonClicked ? 'Cancel Add' : (updButtonClicked ? 'Cancel Update' : (delButtonClicked ? 'Cancel Delete' : ''))}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Spin>
        </>
    );
};

export default AUDDataSetColumnFormats;
